
type TimerCallback = (remaining: number) => void;

interface TimerController {
    pause: () => void;
    resume: () => void;
    stop: () => void;
    isRunning: () => boolean;
}

export function timer(
    sec: number,
    callback?: TimerCallback
): TimerController | Promise<number> {
    let intervalId: NodeJS.Timeout | null = null;
    let remaining = sec;
    let running = true;
    let resolvePromise: ((value: number) => void) | null = null;

    const tick = () => {
        if (!running) return;
        if (remaining > 0) {
            remaining -= 1;
            // eslint-disable-next-line
            callback && callback(remaining);
            if (remaining === 0) {
                stop();
                if (resolvePromise) resolvePromise(0);
            }
        }
    };

    const start = () => {
        if (intervalId) clearInterval(intervalId);
        intervalId = setInterval(tick, 1000);
    };

    const pause = () => {
        running = false;
        if (intervalId) clearInterval(intervalId);
    };

    const resume = () => {
        if (!running && remaining > 0) {
            running = true;
            start();
        }
    };

    const stop = () => {
        running = false;
        if (intervalId) clearInterval(intervalId);
        intervalId = null;
    };

    if (callback) {
        callback(remaining); // initial call
        start();
        return {
            pause,
            resume,
            stop,
            isRunning: () => running && remaining > 0,
        };
    } else {
        // Promise mode
        return new Promise<number>((resolve) => {
            resolvePromise = resolve;
            start();
        });
    }
}


// Example usage in a React component

// export function TimerComponent() {
//     const [remaining, setRemaining] = useState(30);
//     const timerRef = useRef<ReturnType<typeof timer> | null>(null);

//     useEffect(() => {
//         timerRef.current = timer(30, setRemaining);

//         return () => {
//             if (timerRef.current && typeof timerRef.current !== "object") return;
//             timerRef.current?.stop();
//         };
//     }, []);

//     return (
//         <div>
//             <p>Time left: {remaining} seconds</p>
//         </div>
//     );
// }


/**
 * Example usage: Start timer on button click, display countdown from 30 to 0.
 */

// export function TimerOnClickComponent() {
//     const [remaining, setRemaining] = useState<number | null>(null);
//     const timerRef = useRef<ReturnType<typeof timer> | null>(null);

//     const handleStart = () => {
//         // Stop any existing timer
//         if (timerRef.current && typeof timerRef.current === "object") {
//             timerRef.current.stop();
//         }
//         setRemaining(30);
//         timerRef.current = timer(30, setRemaining);
//     };

//     return (
//         <div>
//             <button onClick={handleStart}>Start 30s Timer</button>
//             {remaining !== null && <p>Time left: {remaining} seconds</p>}
//         </div>
//     );
// }